var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, } from 'react';
import { Box, Typography, Grid, IconButton, Divider, } from '@mui/material';
import { ArrowBack, ContentCopy, PlayArrowOutlined, } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
// Internal dependencies
import { appStyles } from '../App.styles';
import { UserContext } from '../auth';
import { axiosClient } from '../axios';
import { LOG_LEVELS, SERVICES, ENDPOINTS, CHAT_CONTEXT, CLIENT_ENDPOINTS } from '../constants';
import { EditIconSG, ButtonSG } from '../components';
import { WorkflowAction } from './WorkflowAction';
import { DEFAULT_WORKFLOW, ACTION_TYPES, getTools } from './constants';
/**
 * Workflow component
 * @param props Props
 * @returns Workflow component
 */
export const Workflow = function ({ workflow, setWorkflow, setCustomWorkflowOpen, setWorkflowOpen, sendMessage, sx, }) {
    // Get user credential
    const user = useContext(UserContext);
    // Get translation function
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const TOOLS = getTools();
    /**
     * Handle Custom workflow open
     */
    const handleCustomWorkflowOpen = () => {
        setCustomWorkflowOpen(true);
        setWorkflowOpen(false);
    };
    /**
     * Handle Clone workflow open
     */
    const handleCloneWorkflowOpen = () => {
        setWorkflow(Object.assign(Object.assign({}, workflow), { name: `${workflow.name} (1)`, _id: undefined, category: undefined, author: undefined, createdAt: undefined, updatedAt: undefined }));
        handleCustomWorkflowOpen();
    };
    /**
     * Run workflow action
     * @param action Workflow action
     * @param messageContent message input
     */
    const runAction = (action, messageContent, workflowStepIdx) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        let resMessageContent = '';
        try {
            const /** Workflow action type */ actionType = action === null || action === void 0 ? void 0 : action.type;
            switch (actionType) {
                case ACTION_TYPES.INPUT:
                    const inputs = action === null || action === void 0 ? void 0 : action.inputs;
                    const inputsContents = inputs.map((input) => (`<${input === null || input === void 0 ? void 0 : input.name}> ${input === null || input === void 0 ? void 0 : input.value} </${input === null || input === void 0 ? void 0 : input.name}>`));
                    const inputsContentsText = inputsContents.join(', ');
                    let metadata = { command: `${action === null || action === void 0 ? void 0 : action.type}`, targetText: `${inputsContentsText}`, workflowStepIdx };
                    yield sendMessage(`Are you ready to answer my questions`, metadata, '', [], CHAT_CONTEXT.WORKFLOW);
                    resMessageContent = inputsContentsText;
                    break;
                case ACTION_TYPES.TOOL:
                    metadata = { command: `Tool`, targetText: `${(_a = TOOLS[action === null || action === void 0 ? void 0 : action.tool]) === null || _a === void 0 ? void 0 : _a.name}`, workflowStepIdx };
                    resMessageContent = yield sendMessage(`${(_b = TOOLS[action === null || action === void 0 ? void 0 : action.tool]) === null || _b === void 0 ? void 0 : _b.promptContent} & ${action === null || action === void 0 ? void 0 : action.promptContent}: ${messageContent}`, metadata, '', [], CHAT_CONTEXT.WORKFLOW);
                    break;
                case ACTION_TYPES.PROMPT:
                    metadata = { command: `${action === null || action === void 0 ? void 0 : action.type}`, targetText: `${action === null || action === void 0 ? void 0 : action.promptContent}`, workflowStepIdx };
                    resMessageContent = yield sendMessage(`${action === null || action === void 0 ? void 0 : action.promptContent}: ${messageContent}`, metadata, '', [], CHAT_CONTEXT.WORKFLOW);
                    break;
            }
            return resMessageContent;
        }
        catch (err) {
            // Log error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Workflow, runAction, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
            return undefined;
        }
        ;
    });
    const ValidateSchema = Yup.object().shape({
        actions: Yup.array().of(Yup.lazy((value) => {
            const /** Action type */ type = value === null || value === void 0 ? void 0 : value.type;
            switch (type) {
                case ACTION_TYPES.INPUT:
                    return Yup.object().shape({
                        inputs: Yup.array().of(Yup.object().shape({
                            value: Yup.string().required('Required'),
                        }))
                    });
                    break;
                case ACTION_TYPES.PROMPT:
                    return Yup.object().shape({});
                    break;
            }
            return Yup.object().shape({});
        })),
    });
    /**
     * Handle back button click
     */
    const handleBackClick = () => { setWorkflowOpen(false); };
    return (_jsx(Box, Object.assign({ sx: Object.assign({ width: '100%', height: '100%' }, sx) }, { children: _jsx(Formik, Object.assign({ initialValues: workflow || Object.assign({}, DEFAULT_WORKFLOW), validationSchema: ValidateSchema, validateOnChange: false, enableReinitialize: true, onSubmit: (values) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d, _e;
                try {
                    if (!user) {
                        navigate(CLIENT_ENDPOINTS.SIGN_IN);
                        return;
                    }
                    let /** Message content for next call */ messageContent = '';
                    const actions = values.actions || [];
                    const actionsLength = actions.length;
                    for (let idx = 0; idx < actionsLength; idx++) {
                        messageContent = yield runAction(actions[idx], messageContent, idx);
                        if (!messageContent) {
                            toast.error(t('Something went wrong. We are figuring it out.'));
                            return false;
                        }
                    }
                }
                catch (err) {
                    // Save error to the system log
                    axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                        level: LOG_LEVELS.ERROR,
                        logMessage: `Workflow, onSubmit, ${err === null || err === void 0 ? void 0 : err.message}`,
                        metadata: {
                            env: process.env.ENV,
                            userId: user === null || user === void 0 ? void 0 : user.uid,
                            email: user === null || user === void 0 ? void 0 : user.email,
                            userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                        },
                    });
                    // Check if the error is from axios
                    if ((_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.status) {
                        // If yes, notify user with the server error message
                        toast.error((_e = (_d = err === null || err === void 0 ? void 0 : err.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.message);
                        return;
                    }
                    ;
                    toast.error(t('Something went wrong. We are figuring it out.'));
                }
                ;
            }) }, { children: (formik) => {
                var _a;
                return (_jsxs(Box, Object.assign({ component: Form, sx: { height: '100%', boxSizing: 'border-box' } }, { children: [_jsxs(Grid, Object.assign({ container: true, columnSpacing: 1, sx: { padding: '0', width: 'calc(100%)' } }, { children: [_jsx(Grid, Object.assign({ item: true, sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                    } }, { children: _jsx(IconButton, Object.assign({ onClick: handleBackClick }, { children: _jsx(ArrowBack, { sx: { color: 'blackDark.main' } }) })) })), _jsx(Grid, Object.assign({ item: true, xs: true, sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '1px'
                                    } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text24), { padding: '0', color: 'black.main', margin: '0 0.5rem 0 0', overflow: 'hidden', textOverflow: 'ellipsis', width: 'calc(100%)', textWrap: 'nowrap' }) }, { children: workflow === null || workflow === void 0 ? void 0 : workflow.name })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(IconButton, Object.assign({ "aria-label": "Clone workflow", onClick: handleCloneWorkflowOpen, sx: {
                                            display: 'inline-flex',
                                        } }, { children: _jsx(ContentCopy, { sx: { color: 'blackDark.main' } }) })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(IconButton, Object.assign({ "aria-label": "Open custom workflow", onClick: handleCustomWorkflowOpen, sx: {
                                            display: 'inline-flex',
                                            color: 'blackDark.main'
                                        }, disabled: (workflow === null || workflow === void 0 ? void 0 : workflow.category) !== 'Custom' ? true : false }, { children: _jsx(EditIconSG, {}) })) }))] })), _jsxs(Box, Object.assign({ sx: { height: 'calc(100% - 40px)', padding: '1.5rem 0 0', overflow: 'auto' } }, { children: [_jsx(Box, Object.assign({ sx: { display: 'block', width: '100%' } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { color: 'greenLighter.main' }) }, { children: workflow === null || workflow === void 0 ? void 0 : workflow.description })) })), _jsx(Box, Object.assign({ sx: {
                                        padding: '1.5rem 0 0 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100%',
                                    } }, { children: _jsxs(Grid, Object.assign({ container: true, sx: { justifyContent: 'center', maxWidth: '416px', width: '100%' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Grid, Object.assign({ container: true, sx: { justifyContent: 'center' } }, { children: _jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG, Object.assign({ variant: 'contained', type: 'submit', sx: Object.assign(Object.assign({}, appStyles.text14), { margin: '0 0 1.5rem 0', padding: '10px 24px 10px 16px', borderRadius: '100px', color: 'white.main', textTransform: 'none' }), startIcon: _jsx(PlayArrowOutlined, { sx: { color: 'white.main' } }) }, { children: t('Run') })) })) })) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Grid, Object.assign({ container: true, sx: {
                                                        textAlign: 'center',
                                                    } }, { children: (((_a = formik === null || formik === void 0 ? void 0 : formik.values) === null || _a === void 0 ? void 0 : _a.actions) || []).map((action, idx) => (_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsxs(Grid, Object.assign({ container: true, sx: { justifyContent: 'center' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12, sx: {
                                                                        display: idx === 0 ? 'none' : 'flex',
                                                                        justifyContent: 'center',
                                                                    } }, { children: _jsx(Divider, { sx: {
                                                                            height: '48px',
                                                                            width: '0',
                                                                            border: '1px solid',
                                                                            borderColor: 'greyLight.main',
                                                                        } }) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(WorkflowAction, { action: action, actionIdx: idx, formik: formik }) }))] })) }), idx))) })) }))] })) }))] }))] })));
            } })) })));
};
